/**
 * Thin orchestrator composing single-order header modal hooks.
 * @module useHeaderModals
 */

import { useSelector } from "react-redux";
import { useInitialDataOfSingleMedicalOrder } from "../../../context/initialDataOfSingleMedicalOrder";
import {
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import { useLogJCodeStatusEvents } from "../../../hooks/logJCodeStatusEvents";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { RootState } from "../../../redux/store/store";
import { useHappyFlowModalSave } from "./headerModals/hooks/useHappyFlowModalSave";
import { useHeaderModalOpenEffect } from "./headerModals/hooks/useHeaderModalOpenEffect";
import { useHeaderModalSecondaryHandlers } from "./headerModals/hooks/useHeaderModalSecondaryHandlers";
import { UseHeaderModalsParams } from "./headerModals/types";

/**
 * Manages modal lifecycle (open, save, close) for the order header.
 *
 * @param params - Order context and provider selection ref
 * @returns Modal save/close handlers and loading state
 */
export const useHeaderModals = ({
  generalOrderDetails,
  authOnFile,
  coverageDetails,
  submissionData,
  selectedProvider,
}: UseHeaderModalsParams) => {
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { oldAuthStatus: oldAuthStatusFromContext } =
    useInitialDataOfSingleMedicalOrder();

  useHeaderModalOpenEffect(openedModalId);

  const logJCodeStatusEvents = useLogJCodeStatusEvents(
    generalOrderDetails as GeneralOrderDetails,
    coverageDetails as SingleCoverage[],
    submissionData ?? ({} as Submission),
  );

  const { handleHappyFlowModalSave, isLoading } = useHappyFlowModalSave({
    generalOrderDetails,
    authOnFile,
    coverageDetails,
    selectedProvider,
    oldAuthStatus: oldAuthStatusFromContext,
    logJCodeStatusEvents,
  });

  const {
    handleNarRpaTriggerClick,
    handleCommentRpaModalSave,
    handleCloseModal,
  } = useHeaderModalSecondaryHandlers({
    generalOrderDetails,
    authOnFile,
    coverageDetails,
    logJCodeStatusEvents,
  });

  return {
    handleHappyFlowModalSave,
    handleNarRpaTriggerClick,
    handleCommentRpaModalSave,
    handleCloseModal,
    isLoading,
  };
};
